import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DSSchoolSelector from '@ajat/design-system/SchoolSelector';
import useSchools from '../../../../hooks/useSchools';
import {
  getSchoolSelection,
  setSchoolSelection,
  clearSchoolSelection,
} from '../../../../services/schoolSession';

/**
 * Format a date string (YYYY-MM-DD) to Swedish display: "D.M.YYYY"
 */
function formatDateSE(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

function loadSelection() {
  return getSchoolSelection();
}

function saveSelection(school, cls) {
  setSchoolSelection({ school, class: cls });
}

const SchoolSelector = ({ isOpen, onClose, onSelect, onNotFoundClick, banner = null, suppressNavigate = false, ...props }) => {
  const navigate = useNavigate();
  const { schools, loading } = useSchools('abcse');

  const [saved, setSaved] = useState(loadSelection);

  const getSchoolInfo = useCallback((school) => {
    if (!school) return null;
    const data = schools.find((s) => s.id === school.id);
    if (!data?.shopOpensAt || !data?.shopClosesAt) return null;

    const now = new Date();
    const opens = new Date(data.shopOpensAt + 'T00:00:00');
    const closes = new Date(data.shopClosesAt + 'T23:59:59');
    const isOpen = now >= opens && now <= closes;

    return {
      statusLabel: isOpen
        ? 'DIN SKOLAS WEBSHOP ÄR ÖPPEN'
        : 'DIN SKOLAS WEBSHOP ÄR STÄNGD',
      dateRange: `${formatDateSE(data.shopOpensAt)} - ${formatDateSE(data.shopClosesAt)}`,
      description: isOpen
        ? 'Glöm inte att köpa din studentmössa under denna period för att få rabatt.'
        : null,
    };
  }, [schools]);

  const handleSelect = useCallback((selection) => {
    saveSelection(selection.school, selection.class);
    setSaved({ school: selection.school, class: selection.class });
    onSelect?.(selection);
    // A pending menu-item intent navigates from the Header instead — don't
    // clobber it with the default school-shop route.
    if (!suppressNavigate && selection?.school?.slug) {
      navigate(`/clothing/abc-se/school-shop/${selection.school.slug}`);
    }
  }, [onSelect, navigate, suppressNavigate]);

  const handleClear = useCallback(() => {
    clearSchoolSelection();
    setSaved(null);
    onSelect?.(null);
  }, [onSelect]);

  return (
    <DSSchoolSelector
      isOpen={isOpen}
      onClose={onClose}
      onSelect={handleSelect}
      onNotFoundClick={onNotFoundClick}
      banner={banner || require('../../../../assets/abc-se/common/school-selector-banner.jpg')}
      // Only schools that currently have an open shop are selectable in the
      // popup (the API's `seasons` are the open+active shops).
      schools={loading ? [] : schools.filter((s) => (s.seasons ?? []).length > 0)}
      getSchoolInfo={getSchoolInfo}
      onClear={handleClear}
      clearLabel="Ta bort skolval"
      initialSchool={saved?.school ?? null}
      initialClass={saved?.class ?? null}
      requireClass={false}
      title="Välj din skola"
      infoText="Välj din skola för att få tillgång till unika priser och produkter"
      schoolPlaceholder="Skriv in din skola"
      submitLabel="Fortsätt"
      {...props}
    />
  );
};

export default SchoolSelector;
